import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  Renderer2,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { UserEnvService } from '@core/services/user-env/user-env.service';
import {
  getUserDateFormat,
  momentFormatToInputMask,
  momentFormatToPlaceholder,
  momentFormatToPrimeNg,
} from '../../utils/kl-date-format';
import { resolveBootstrapColWidths } from '../../utils/bootstrap-col-widths';

@Component({
  selector: 'dk-date-time-picker',
  standalone: false,
  templateUrl: './dk-date-time-picker.html',
  styleUrl: './dk-date-time-picker.scss',
})
export class DkDateTimePickerComponent implements OnInit {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly userEnv = inject(UserEnvService);

  readonly formGroup = input.required<FormGroup>();
  readonly dateControlName = input.required<string>();
  readonly timeControlName = input.required<string>();

  readonly label = input('');
  readonly labelDescription = input('');

  readonly dateInputId = input('');
  /** i18n key for moment-style parse pattern (`date_format_pattern` in lang files). */
  readonly dateFormatPatternKey = input('date_format_pattern');
  /** i18n key for localized empty-state hint (`date_format_placeholder` in lang files). */
  readonly dateFormatPlaceholderKey = input('date_format_placeholder');
  /** Optional fixed moment-style pattern; overrides locale/user-env when set. */
  readonly dateFormat = input<string | undefined>(undefined);
  /** Optional placeholder override (display text, e.g. gg.aa.yyyy). */
  readonly datePlaceholder = input<string | undefined>(undefined);

  private readonly resolvedMomentPattern = signal(getUserDateFormat());
  private readonly resolvedPlaceholder = signal(momentFormatToPlaceholder(getUserDateFormat()));

  readonly effectivePrimeDateFormat = computed(() =>
    momentFormatToPrimeNg(this.dateFormat() ?? this.resolvedMomentPattern())
  );

  readonly effectiveDatePlaceholder = computed(() => this.datePlaceholder() ?? this.resolvedPlaceholder());

  readonly enableMask = input(true);

  readonly effectiveDateMask = computed(() => {
    if (!this.enableMask()) return undefined;
    return momentFormatToInputMask(this.dateFormat() ?? this.resolvedMomentPattern());
  });

  readonly showIcon = input(true);
  readonly iconDisplay = input<'button' | 'input'>('input');
  readonly showClear = input(true);
  readonly showOnFocus = input(true);
  readonly appendTo = input<unknown>('body');
  readonly minDate = input<Date | undefined>(undefined);
  readonly maxDate = input<Date | undefined>(undefined);

  readonly timeInputId = input('');
  readonly hourFormat = input<'12' | '24'>('12');
  readonly showSeconds = input(false);

  readonly dateCols = input('7');
  readonly timeCols = input('5');

  readonly col = input('12');
  readonly sm = input<string | undefined>(undefined);
  readonly md = input<string | undefined>(undefined);
  readonly lg = input<string | undefined>(undefined);
  readonly xl = input<string | undefined>(undefined);
  readonly marginY = input(false);
  readonly marginBottom = input(true);

  readonly showFieldError = input(false);

  readonly dateValueChange = output<unknown>();
  readonly timeValueChange = output<unknown>();

  /** Standalone model for time-only picker (parent control stores HH:mm string, HH:mm:ss with `showSeconds`). */
  timePickerValue: Date | null = null;

  readonly dateControl = computed<AbstractControl | null>(() => this.formGroup()?.get(this.dateControlName()) ?? null);

  readonly timeControl = computed<AbstractControl | null>(() => this.formGroup()?.get(this.timeControlName()) ?? null);

  /**
   * `control.invalid`/`.touched`/`.errors` are plain mutable state, not signals, so a `computed()`
   * reading them never re-runs on its own — e.g. `form.markAllAsTouched()` from a save handler
   * wouldn't turn on this field's red border. Bumped on every date/time control event (see
   * constructor) so the validation-derived computeds below stay live.
   */
  private readonly controlState = signal(0);

  readonly dateFieldId = computed(() =>
    (this.dateInputId() || this.dateControlName() || 'dk-date').replace(/\s/g, '_')
  );

  readonly timeFieldId = computed(() =>
    (this.timeInputId() || this.timeControlName() || 'dk-time').replace(/\s/g, '_')
  );

  readonly required = computed(() => {
    this.controlState();
    const dc = this.dateControl();
    const tc = this.timeControl();
    if (!dc?.validator && !tc?.validator) return false;
    const dv = dc?.validator?.({} as AbstractControl);
    const tv = tc?.validator?.({} as AbstractControl);
    return !!(dv?.['required'] || tv?.['required']);
  });

  readonly showInvalid = computed(() => {
    this.controlState();
    const dc = this.dateControl();
    const tc = this.timeControl();
    const dateInvalid = dc && dc.invalid && dc.touched;
    const timeInvalid = tc && tc.invalid && tc.touched;
    return !!(dateInvalid || timeInvalid);
  });

  readonly controlError = computed(() => {
    this.controlState();
    const dc = this.dateControl();
    const tc = this.timeControl();
    if (dc?.invalid && dc.touched && dc.errors?.['required']) {
      return 'Date is required.';
    }
    if (tc?.invalid && tc.touched && tc.errors?.['required']) {
      return 'Time is required.';
    }
    return null;
  });

  constructor() {
    effect(() => {
      this.userEnv.env();
      this.refreshDatePattern();
    });

    effect((onCleanup) => {
      const dateSub = this.dateControl()?.events.subscribe(() => this.controlState.update((v) => v + 1));
      const timeSub = this.timeControl()?.events.subscribe(() => this.controlState.update((v) => v + 1));
      onCleanup(() => {
        dateSub?.unsubscribe();
        timeSub?.unsubscribe();
      });
    });

    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.refreshDatePattern();
    });
  }

  ngOnInit(): void {
    this.refreshDatePattern();

    const el = this.host.nativeElement;
    const col = this.col();
    const { sm, md, lg, xl } = resolveBootstrapColWidths(col, this.sm(), this.md(), this.lg(), this.xl());
    this.renderer.addClass(el, 'col-' + col);
    this.renderer.addClass(el, 'col-sm-' + sm);
    this.renderer.addClass(el, 'col-md-' + md);
    this.renderer.addClass(el, 'col-lg-' + lg);
    this.renderer.addClass(el, 'col-xl-' + xl);

    this.syncTimePickerFromControl();
    const tc = this.timeControl();
    tc?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.syncTimePickerFromControl();
    });
  }

  private refreshDatePattern(): void {
    if (!this.dateFormat()) {
      const envFmt = this.userEnv.env()?.date_format;
      if (envFmt) {
        this.resolvedMomentPattern.set(envFmt);
      } else {
        const patternKey = this.dateFormatPatternKey();
        const translatedPattern = this.translate.instant(patternKey);
        if (translatedPattern && translatedPattern !== patternKey) {
          this.resolvedMomentPattern.set(translatedPattern);
        } else {
          this.resolvedMomentPattern.set(getUserDateFormat());
        }
      }
    }

    const placeholderKey = this.dateFormatPlaceholderKey();
    const translatedPlaceholder = this.translate.instant(placeholderKey);
    if (translatedPlaceholder && translatedPlaceholder !== placeholderKey) {
      this.resolvedPlaceholder.set(translatedPlaceholder);
    } else {
      this.resolvedPlaceholder.set(momentFormatToPlaceholder(this.resolvedMomentPattern()));
    }
  }

  onDateSelect(): void {
    this.dateValueChange.emit(this.dateControl()?.value);
  }

  onTimeSelect(): void {
    const formatted = this.formatTimeForControl(this.timePickerValue);
    const current = this.timeControl()?.value;
    if (formatted === current) return;
    this.timeControl()?.setValue(formatted, { emitEvent: true });
    this.timeValueChange.emit(formatted);
  }

  onTimeClear(): void {
    this.timePickerValue = null;
    this.onTimeSelect();
  }

  private syncTimePickerFromControl(): void {
    this.timePickerValue = this.parseTimeToDate(this.timeControl()?.value);
  }

  private parseTimeToDate(value: unknown): Date | null {
    if (value == null || value === '') return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const d = new Date();
      d.setHours(value.getHours(), value.getMinutes(), value.getSeconds(), 0);
      return d;
    }

    const s = String(value).trim();
    const d = new Date();
    d.setSeconds(0, 0);

    const ampm = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (ampm) {
      let h = Number(ampm[1]);
      const m = Number(ampm[2]);
      const sec = Number(ampm[3]) || 0;
      if (ampm[4].toUpperCase() === 'PM' && h < 12) h += 12;
      if (ampm[4].toUpperCase() === 'AM' && h === 12) h = 0;
      d.setHours(h, m, sec);
      return d;
    }

    const parts = s.split(':');
    if (parts.length >= 2) {
      d.setHours(Number(parts[0]) || 0, Number(parts[1]) || 0, Number(parts[2]) || 0);
      return d;
    }

    return null;
  }

  /** Persist as 24h HH:mm (HH:mm:ss with `showSeconds`) for receipt save helpers. */
  private formatTimeForControl(date: Date | null): string {
    if (!date || Number.isNaN(date.getTime())) return '00:00';
    const h = date.getHours();
    const m = date.getMinutes();
    const hhmm = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    return this.showSeconds() ? `${hhmm}:${String(date.getSeconds()).padStart(2, '0')}` : hhmm;
  }
}
